/**
 * InboxBell — جرس إشعارات داخل التطبيق للعميل المسجَّل.
 * يقرأ من customer_inbox_notifications عبر listInboxForUser.
 * يُستخدم في صفحة /account.
 */
import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Bell } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocale } from "@/contexts/LocaleContext";
import { useAuth } from "@/contexts/AuthContext";
import { listInboxForUser, markInboxRead } from "@/utils/inbox.functions";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export function InboxBell() {
  const { user } = useAuth();
  const { locale, dir } = useLocale();
  const isAr = locale === "ar";
  const qc = useQueryClient();

  const { data: items = [] } = useQuery({
    queryKey: ["inbox", user?.id],
    queryFn: () => listInboxForUser(),
    enabled: !!user,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => markInboxRead({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["inbox"] }),
  });

  if (!user) return null;

  // إخفاء الإشعارات المقروءة قبل بداية اليوم الحالي (تختفي في اليوم التالي)
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const visibleItems = (items as any[]).filter((n) => {
    if (!n.read_at) return true;
    return new Date(n.read_at).getTime() >= startOfToday.getTime();
  });

  const unread = visibleItems.filter((n: any) => !n.read_at).length;
  const pick = (n: any) => ({
    title: isAr ? n.title_ar || n.title_en : n.title_en || n.title_ar,
    body: isAr ? n.body_ar || n.body_en : n.body_en || n.body_ar,
  });

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const startToday = new Date(now); startToday.setHours(0, 0, 0, 0);
    const startYday = new Date(startToday); startYday.setDate(startYday.getDate() - 1);
    if (d >= startToday) {
      return d.toLocaleTimeString(isAr ? "ar" : "en", { hour: "2-digit", minute: "2-digit" });
    }
    if (d >= startYday) return isAr ? "أمس" : "Yesterday";
    return d.toLocaleDateString(isAr ? "ar" : "en", { day: "2-digit", month: "short" });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={isAr ? "الإشعارات" : "Notifications"}
          className="relative rounded-full p-2 text-foreground/80 transition-colors hover:text-primary"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -end-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 t-micro font-medium text-destructive-foreground">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        dir={dir}
        sideOffset={8}
        alignOffset={0}
        collisionPadding={12}
        avoidCollisions
        className="z-[60] w-[min(92vw,360px)] overflow-hidden rounded-none border border-border bg-popover p-0 "
      >
        <div className="border-b border-border px-4 py-3">
          <p className="text-sm font-medium text-foreground">
            {isAr ? "الإشعارات" : "Notifications"}
          </p>
        </div>
        <ul className="max-h-[60vh] divide-y divide-border overflow-auto">
          {visibleItems.length === 0 && (
            <li className="px-4 py-6 text-center text-xs text-muted-foreground">
              {isAr ? "لا توجد إشعارات" : "No notifications yet"}
            </li>
          )}
          {visibleItems.map((n: any) => {
            const m = pick(n);
            const unreadItem = !n.read_at;
            const handleClick = () => {
              if (unreadItem) markRead.mutate(n.id);
            };
            const Inner = (
              <div
                className={`flex flex-col gap-1 px-4 py-3 text-start ${unreadItem ? "bg-accent/30 border-s-2 border-primary" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                    {formatDate(n.created_at)}
                  </span>
                  {unreadItem && (
                    <span className="inline-block h-2 w-2 rounded-full bg-primary" aria-hidden />
                  )}
                </div>
                <p className={`t-body-sm ${unreadItem ? "font-medium text-foreground" : "text-foreground"}`}>{m.title}</p>
                {m.body && (
                  <p className="text-xs text-muted-foreground">{m.body}</p>
                )}
              </div>
            );
            return (
              <li key={n.id}>
                {n.link_url ? (
                  <Link
                    to={n.link_url}
                    onClick={handleClick}
                    className="block hover:bg-muted"
                  >
                    {Inner}
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={handleClick}
                    className="block w-full hover:bg-muted"
                  >
                    {Inner}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
